/* @jsx h */
import { computed, defineComponent, h } from "vue";
import { useLocalization } from "@lemmonhq/uikit";
import { Reservation } from "@lemmonhq/types";
import { formatDate, formatTime } from "./utils";
import { useAuth } from "@app/vue/providers/Auth";

export interface ReservationCardProps {
  reservation: Reservation;
  onClick: (reservation: Reservation) => void;
}

const getStatusConfig = (
  t: (key: string) => string,
): Record<
  string,
  { label: string; bgClass: string; textClass: string; icon: string }
> => ({
  confirmed: {
    label: t("reservations.status.confirmed"),
    bgClass: "bg-success",
    textClass: "text-success-content",
    icon: "check_circle",
  },
  seated: {
    label: t("reservations.status.seated"),
    bgClass: "bg-info",
    textClass: "text-info-content",
    icon: "event_seat",
  },
  "partially-seated": {
    label: t("reservations.status.partially_seated"),
    bgClass: "bg-info",
    textClass: "text-info-content",
    icon: "event_seat",
  },
  completed: {
    label: t("reservations.status.completed"),
    bgClass: "bg-base-content/20",
    textClass: "text-base-content/70",
    icon: "done_all",
  },
  "no-show": {
    label: t("reservations.status.no_show"),
    bgClass: "bg-error",
    textClass: "text-error-content",
    icon: "person_off",
  },
  cancelled_by_host: {
    label: t("reservations.status.cancelled_by_host"),
    bgClass: "bg-base-content/15",
    textClass: "text-base-content/60",
    icon: "cancel",
  },
  cancelled_by_user: {
    label: t("reservations.status.cancelled_by_user"),
    bgClass: "bg-error/15",
    textClass: "text-error",
    icon: "cancel",
  },
});

export const ReservationCard = defineComponent<ReservationCardProps>({
  name: "ReservationCard",
  props: ["reservation", "onClick"],
  setup(props) {
    const { user } = useAuth();
    const { t } = useLocalization();
    const timezone = user.value?.restaurant?.timezone || "UTC";
    const statusConfigMap = computed(() => getStatusConfig(t));

    return () => {
      const reservation = props.reservation;
      const statusConfig = statusConfigMap.value[reservation.status] || {
        label: reservation.status,
        bgClass: "bg-base-content/15",
        textClass: "text-base-content/60",
        icon: "help",
      };
      const hasTags = reservation.tags && reservation.tags.length > 0;
      const hasRequestedTags =
        reservation.requested_tags && reservation.requested_tags.length > 0;

      const reservationTime = new Date(reservation.time);
      const now = new Date();
      const diffMs = reservationTime.getTime() - now.getTime();
      const isArrivingSoon =
        reservation.status === "confirmed" &&
        diffMs > 0 &&
        diffMs <= 30 * 60 * 1000;
      const isArriving =
        reservation.status === "confirmed" &&
        diffMs <= 0 &&
        diffMs > -15 * 60 * 1000;

      // Overtime for seated reservations (show only if >= 1h, in hours only)
      const endTime =
        reservationTime.getTime() + reservation.duration_mins * 60000;
      const overtimeHours = Math.floor((now.getTime() - endTime) / 3600000);
      const isOvertime = reservation.status === "seated" && overtimeHours >= 1;

      // Late for confirmed reservations (show only if >= 1h, in hours only)
      const lateHours = Math.floor(Math.abs(diffMs) / 3600000);
      const isLate =
        reservation.status === "confirmed" && diffMs < 0 && lateHours >= 1;

      // Dynamic status: Soon → Arriving → normal status
      let displayLabel = statusConfig.label;
      let displayBg = statusConfig.bgClass;
      let displayText = statusConfig.textClass;
      let displayIcon = statusConfig.icon;

      if (isArriving) {
        displayLabel = "Arriving";
        displayBg = "bg-error/15 border border-error/60";
        displayText = "text-error";
        displayIcon = "directions_walk";
      } else if (isArrivingSoon) {
        displayLabel = "Soon";
        displayBg = "bg-warning/15 border border-warning/60";
        displayText = "text-warning";
        displayIcon = "schedule";
      }

      // Shared table badge renderer
      const tableBadge = () => {
        if (
          reservation.table_assignments &&
          reservation.table_assignments.length > 0
        ) {
          const names = reservation.table_assignments.map((t) => t.table_name);
          const label = names.join(", ");
          return (
            <div
              class="inline-flex min-w-0 w-full items-center justify-center gap-1 px-3 py-1 rounded-md text-xs font-medium bg-purple-500/10 text-purple-400 border border-purple-500/20"
              title={label}
            >
              <span class="material-symbols-outlined text-sm flex-shrink-0">
                table_restaurant
              </span>
              <span class="truncate min-w-0">{label}</span>
            </div>
          );
        }
        return (
          <div class="inline-flex w-full items-center justify-center gap-1 px-3 py-1 rounded-md text-xs font-medium bg-base-content/5 text-base-content/30 border border-base-content/10">
            <span class="material-symbols-outlined text-sm">
              table_restaurant
            </span>
            <span class="text-[10px]">No table</span>
          </div>
        );
      };

      return (
        <div
          class="bg-base-100 rounded-lg shadow-sm hover:shadow-md transition-all cursor-pointer group border border-base-300/30 hover:border-base-content/20 overflow-hidden"
          onClick={() => props.onClick(reservation)}
        >
          <div class="px-3 py-3.5 sm:px-4">
            {/* Row 1: Time | Status | Name (+ email on desktop) | Party size + duration (desktop) | Tags/Notes/Table (desktop) */}
            <div class="grid grid-cols-[80px_minmax(0,1fr)_auto] items-center gap-3 sm:grid-cols-[80px_105px_minmax(0,1fr)_minmax(120px,160px)] sm:gap-4 md:grid-cols-[80px_105px_180px_auto_minmax(0,1fr)_minmax(120px,160px)] md:gap-6">
              {/* 1. Time */}
              <div class="flex-shrink-0 self-start" style="width: 80px;">
                <span class="text-base sm:text-xl font-bold text-accent leading-tight whitespace-nowrap">
                  {formatTime(reservation.time, timezone)}
                </span>
                <div class="text-[10px] text-base-content/50 mt-0.5 font-medium uppercase tracking-wide">
                  {formatDate(reservation.time, timezone, {
                    today: t("general.date.today"),
                    tomorrow: t("general.date.tomorrow"),
                  })}
                </div>
              </div>

              {/* 2. Status badge — hidden on mobile */}
              <div class="flex-shrink-0 hidden sm:block" style="width: 105px;">
                <div
                  class={`uppercase relative flex items-center w-full px-1 rounded-md text-[10px] font-semibold leading-tight ${displayBg} ${displayText}`}
                  style={`height: 32px;${isArriving ? " animation: pulse 1.5s cubic-bezier(0.4, 0, 0.6, 1) infinite;" : isArrivingSoon ? " animation: pulse 3s cubic-bezier(0.4, 0, 0.6, 1) infinite;" : ""}`}
                >
                  <span class="material-symbols-outlined text-[12px] absolute left-1 top-1/2 -translate-y-1/2">
                    {displayIcon}
                  </span>
                  <span class="leading-tight w-full text-center pl-3">
                    {displayLabel}
                  </span>
                </div>
              </div>

              {/* 3. Guest Name + party details */}
              <div class="min-w-0">
                <h3 class="font-semibold text-accent text-sm sm:text-base truncate">
                  {reservation.party_name ||
                    t("reservations.general.guest_label")}
                </h3>
                <div class="flex md:hidden items-center gap-2 mt-0.5">
                  <span class="flex items-center gap-0.5 text-xs text-base-content/70">
                    <span class="material-symbols-outlined text-xs">group</span>
                    {reservation.party_size}
                  </span>
                  <span class="flex items-center gap-0.5 text-xs text-base-content/55">
                    <span class="material-symbols-outlined text-xs">
                      schedule
                    </span>
                    {reservation.duration_mins}m
                  </span>
                </div>
              </div>

              {/* 4. Party size + duration + timing alerts — desktop only */}
              <div class="hidden md:flex min-w-0 flex-col items-start gap-1">
                <div class="flex items-center gap-3">
                  <div class="flex items-center gap-1 text-sm text-base-content/70">
                    <span class="material-symbols-outlined text-base">
                      group
                    </span>
                    <span class="font-medium">{reservation.party_size}</span>
                  </div>
                  <div class="flex items-center gap-1 text-xs text-base-content/55">
                    <span class="material-symbols-outlined text-sm">
                      schedule
                    </span>
                    <span>{reservation.duration_mins} min</span>
                  </div>
                </div>
                {(isOvertime || isLate) && (
                  <div class="flex min-w-0 items-center gap-1">
                    {isOvertime && (
                      <div class="flex items-center gap-1 text-xs font-medium px-1.5 py-0.5 rounded-md bg-warning/10 text-warning/80">
                        <span class="material-symbols-outlined text-sm">
                          timer_off
                        </span>
                        <span>+{overtimeHours}h</span>
                      </div>
                    )}
                    {isLate && (
                      <div class="flex items-center gap-1 text-xs font-medium px-1.5 py-0.5 rounded-md bg-warning/10 text-warning/80">
                        <span class="material-symbols-outlined text-sm">
                          warning
                        </span>
                        <span>{lateHours}h late</span>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* 5. Tags + Notes + Comments — tablet and desktop */}
              <div class="hidden md:flex min-w-0 items-center gap-1.5 justify-end overflow-hidden">
                {hasTags &&
                  reservation.tags.map((tag: any) => (
                    <span
                      key={tag.id}
                      class="inline-flex min-w-0 max-w-[120px] shrink items-center gap-1 px-2 py-1 rounded-md text-xs font-medium border border-current/15"
                      style={{
                        backgroundColor: tag.color
                          ? `${tag.color}15`
                          : "oklch(var(--b3))",
                        color: tag.color || undefined,
                      }}
                      title={tag.name}
                    >
                      {tag.icon && (
                        <span class="material-symbols-outlined text-sm flex-shrink-0">
                          {tag.icon}
                        </span>
                      )}
                      <span class="truncate min-w-0">{tag.name}</span>
                    </span>
                  ))}
                {reservation.notes && (
                  <div
                    class="inline-flex min-w-0 max-w-[180px] shrink items-center gap-1 px-2 py-1 rounded-md text-xs font-medium bg-teal-500/10 text-teal-400 border border-teal-500/20"
                    title={reservation.notes}
                  >
                    <span class="material-symbols-outlined text-sm flex-shrink-0">
                      sticky_note_2
                    </span>
                    <span class="truncate min-w-0">{reservation.notes}</span>
                  </div>
                )}
                {reservation.comments?.length > 0 && (
                  <div class="inline-flex min-w-0 max-w-[100px] shrink-0 items-center gap-1 px-2 py-1 rounded-md text-xs font-medium bg-amber-500/10 text-amber-400 border border-amber-500/20">
                    <span class="material-symbols-outlined text-sm flex-shrink-0">
                      chat
                    </span>
                    <span class="truncate">
                      {reservation.comments.length} notes
                    </span>
                  </div>
                )}
              </div>

              {/* Mobile: status badge. Table assignments live in the details row. */}
              <div class="flex sm:hidden items-center gap-1.5 justify-end min-w-0">
                <span
                  class={`uppercase inline-flex max-w-[112px] items-center gap-1 rounded-md px-1.5 py-1 text-[10px] font-semibold leading-tight ${displayBg} ${displayText}`}
                  style={
                    isArriving
                      ? "animation: pulse 1.5s cubic-bezier(0.4, 0, 0.6, 1) infinite;"
                      : isArrivingSoon
                        ? "animation: pulse 3s cubic-bezier(0.4, 0, 0.6, 1) infinite;"
                        : ""
                  }
                >
                  <span class="material-symbols-outlined text-[12px] flex-shrink-0">
                    {displayIcon}
                  </span>
                  <span class="truncate">{displayLabel}</span>
                </span>
              </div>

              {/* Desktop: Table */}
              <div class="hidden sm:flex min-w-0 md:col-start-6">
                {tableBadge()}
              </div>
            </div>

            {/* Mobile: Additional details row */}
            <div class="flex md:hidden items-center gap-2 p-2 border-t border-base-300/30 flex-wrap">
              {(isOvertime || isLate) && (
                <div class="flex basis-full items-center gap-1">
                  {isOvertime && (
                    <span class="inline-flex items-center gap-0.5 rounded-md bg-warning/10 px-1.5 py-0.5 text-xs font-medium text-warning/80">
                      <span class="material-symbols-outlined text-xs">
                        timer_off
                      </span>
                      +{overtimeHours}h
                    </span>
                  )}
                  {isLate && (
                    <span class="inline-flex items-center gap-0.5 rounded-md bg-warning/10 px-1.5 py-0.5 text-xs font-medium text-warning/80">
                      <span class="material-symbols-outlined text-xs">
                        warning
                      </span>
                      {lateHours}h late
                    </span>
                  )}
                </div>
              )}
              {reservation.notes && (
                <div class="flex items-center gap-1 text-xs text-base-content/60 flex-1 min-w-0">
                  <span class="material-symbols-outlined text-sm">chat</span>
                  <span class="truncate">{reservation.notes}</span>
                </div>
              )}
              {reservation.table_assignments &&
                reservation.table_assignments.length > 0 && (
                  <div class="flex items-center gap-1 text-xs text-base-content/60 min-w-0 max-w-full">
                    <span class="material-symbols-outlined text-sm">
                      table_restaurant
                    </span>
                    <span class="truncate min-w-0">
                      {reservation.table_assignments
                        .map((t) => t.table_name)
                        .join(", ")}
                    </span>
                  </div>
                )}
              {hasRequestedTags &&
                reservation.requested_tags!.map((tag) => (
                  <span
                    key={`req-${tag.id}`}
                    class="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium border border-current/15"
                    style={{
                      backgroundColor: tag.color
                        ? `${tag.color}20`
                        : "oklch(var(--b3))",
                      color: tag.color || undefined,
                    }}
                  >
                    {tag.icon && (
                      <span class="material-symbols-outlined text-xs">
                        {tag.icon}
                      </span>
                    )}
                    {tag.name}
                  </span>
                ))}
              {hasTags &&
                reservation.tags.map((tag) => (
                  <span
                    key={tag.id}
                    class="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium border border-current/15"
                    style={{
                      backgroundColor: tag.color
                        ? `${tag.color}15`
                        : "oklch(var(--b3))",
                      color: tag.color || undefined,
                    }}
                  >
                    {tag.icon && (
                      <span class="material-symbols-outlined text-xs">
                        {tag.icon}
                      </span>
                    )}
                    {tag.name}
                  </span>
                ))}
            </div>
          </div>
        </div>
      );
    };
  },
});
